'use client';

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { rootReducer, initialState } from 'src/context/reducers';
import { SiteActionTypes } from 'src/context/reducers/actionTypes';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  selectPageFilterBar,
  setPageFilterBar,
} from 'src/redux/slices/uiPreferencesSlice';

const SiteContext = createContext<{
  state: typeof initialState;
  dispatch: React.Dispatch<any>;
}>({
  state: initialState,
  dispatch: () => null,
});

export const SiteProvider = ({ children }: { children: ReactNode }) => {
  const reduxPageFilterBar = useAppSelector(selectPageFilterBar);
  const reduxDispatch = useAppDispatch();
  const hasHydrated = useRef(false);
  const skipNextPersist = useRef(false);

  const [state, dispatch] = useReducer(rootReducer, initialState);

  // Hydrate from Redux when settings are loaded
  useEffect(() => {
    if (reduxPageFilterBar && !hasHydrated.current) {
      hasHydrated.current = true;
      // Skip the next persist to avoid writing hydrated data back to Redux
      skipNextPersist.current = true;
      dispatch({
        type: SiteActionTypes.HYDRATE_SITE_PROVIDER,
        payload: reduxPageFilterBar,
      });
    }
  }, [reduxPageFilterBar]);

  // Persist to Redux when siteProvider changes (replaces localStorage)
  useEffect(() => {
    if (!hasHydrated.current) return;
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      return;
    }
    reduxDispatch(setPageFilterBar(state.siteProvider));
  }, [state.siteProvider, reduxDispatch]);

  const value = useMemo(
    () => ({ state: state as typeof initialState, dispatch }),
    [state, dispatch]
  );

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) throw new Error('useSite must be used within a SiteProvider');
  return context;
};
